import { useEffect, useRef } from 'react';
import { useIsDesktop } from 'Hooks';
import { useSearchLogic } from './hooks/useSearchLogic';
import { SearchDropdown } from './SearchDropdown';
import { SearchCurtain } from './SearchCurtain';
import { SearchModal } from './SearchModal';

export type { IResult, IRelatedAction } from './hooks/useSearchLogic';

interface IActivitiesSearch {
  placeholder?: string;
  source?: string;
  initialOpen?: boolean;
  onClose?: () => void;
}

export const ActivitiesSearchBar = ({
  placeholder,
  source,
  initialOpen = false,
  onClose,
}: IActivitiesSearch) => {
  const isDesktop = useIsDesktop();
  const prevInitialOpenRef = useRef(initialOpen);

  const {
    isActive,
    setIsActive,
    value,
    results,
    isLoading,
    relatedActions,
    inputRef,
    searchBarRef,
    containerRef,
    onActivityClick,
    onCloseSearch,
    handleInputChange,
    handleKeyDown,
    clearValue,
    showNoResults,
    showResults,
    hasContent,
    MIN_SEARCH_LENGTH,
  } = useSearchLogic({
    initialOpen,
    onClose,
    isDesktop,
  });

  const useDropdown = isDesktop && source === 'home';
  const useCurtain = source === 'header' || !isDesktop;

  useEffect(() => {
    if (source === 'header') {
      setIsActive(initialOpen);
      if (prevInitialOpenRef.current && !initialOpen) {
        onCloseSearch();
      }
      prevInitialOpenRef.current = initialOpen;
    }
  }, [initialOpen, source, setIsActive, onCloseSearch]);

  useEffect(() => {
    if (!useDropdown) {
      window.scrollTo(0, 0);
    }
    setTimeout(() => {
      if (isActive && inputRef.current) inputRef.current.focus();
    }, 0);
  }, [isActive, useDropdown, inputRef]);

  if (useDropdown) {
    return (
      <SearchDropdown
        isActive={isActive}
        value={value}
        results={results}
        relatedActions={relatedActions}
        isLoading={isLoading}
        showNoResults={showNoResults}
        showResults={showResults}
        hasContent={hasContent}
        minSearchLength={MIN_SEARCH_LENGTH}
        placeholder={placeholder}
        containerRef={containerRef}
        searchBarRef={searchBarRef}
        inputRef={inputRef}
        onActivate={() => setIsActive(true)}
        onClose={onCloseSearch}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClear={clearValue}
        onActivityClick={onActivityClick}
      />
    );
  }

  if (useCurtain) {
    return (
      <SearchCurtain
        isActive={isActive}
        value={value}
        results={results}
        relatedActions={relatedActions}
        isLoading={isLoading}
        showNoResults={showNoResults}
        showResults={showResults}
        hasContent={hasContent}
        minSearchLength={MIN_SEARCH_LENGTH}
        placeholder={placeholder}
        source={source}
        inputRef={inputRef}
        searchBarRef={searchBarRef}
        onActivate={() => setIsActive(true)}
        onClose={onCloseSearch}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClear={clearValue}
        onActivityClick={onActivityClick}
      />
    );
  }

  return (
    <SearchModal
      isActive={isActive}
      value={value}
      results={results}
      relatedActions={relatedActions}
      isLoading={isLoading}
      showNoResults={showNoResults}
      showResults={showResults}
      minSearchLength={MIN_SEARCH_LENGTH}
      placeholder={placeholder}
      source={source}
      searchBarRef={searchBarRef}
      inputRef={inputRef}
      onActivate={() => setIsActive(true)}
      onClose={onCloseSearch}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onClear={clearValue}
      onActivityClick={onActivityClick}
    />
  );
};
